// src/pages/banner-module/ReadBanners.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardWrapper from "../../../components/DashboardWrapper";
import CrudTable from "../../../components/dashboard/table/CrudTable";
import bannerService from "../../../services/bannerService";
import { useCrud } from "../../../hooks/useCrud";
import Breadcrumb from "../../../components/Breadcrumbdashboard";

const localCacheKey = "bannerReadCache";

export default function ReadBanners() {
  const navigate = useNavigate();
  const { deleteItem } = useCrud("backend/banners");

  const [cachedData, setCachedData] = useState(() => {
    const cache = localStorage.getItem(localCacheKey);
    return cache ? JSON.parse(cache) : null;
  });

  const [localData, setLocalData] = useState(cachedData?.banners || []);
  const [loading, setLoading] = useState(!cachedData);

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const bannerRes = await bannerService.getAllBanners();
      const banners = bannerRes.data.data || bannerRes.data;

      const newCache = { banners };
      const existingCache = localStorage.getItem(localCacheKey);
      const parsedCache = existingCache ? JSON.parse(existingCache) : null;

      if (!parsedCache || JSON.stringify(parsedCache.banners) !== JSON.stringify(banners)) {
        localStorage.setItem(localCacheKey, JSON.stringify(newCache));
        setCachedData(newCache);
      }

      setLocalData(banners);
    } catch (error) {
      console.error("Failed to load banners:", error);
      showSwal("Error", "Failed to load banner data.", "error");
    } finally {
      setLoading(false);
    }
  }, [showSwal]);

  useEffect(() => {
    if (cachedData) {
      setLocalData(cachedData.banners);
      setLoading(false);
      loadData(); // background refresh
    } else {
      loadData();
    }
  }, [cachedData, loadData]);

  const updateLocalDataCache = (updatedBanners) => {
    const newCache = { banners: updatedBanners };
    setLocalData(updatedBanners);
    setCachedData(newCache);
    localStorage.setItem(localCacheKey, JSON.stringify(newCache));
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "btn btn-primary m-1",
        cancelButton: "btn btn-danger m-1",
      },
      buttonsStyling: false,
    });

    if (confirm.isConfirmed) {
      try {
        await deleteItem(id);
        const updated = localData.filter((item) => item.id !== id);
        updateLocalDataCache(updated);
        showSwal("Deleted!", "Banner has been deleted.", "success");
      } catch (error) {
        console.error("Delete error:", error);
        showSwal("Error", "Failed to delete banner.", "error");
      }
    }
  };

  // ✅ Optional: handle active/inactive toggle
  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        await bannerService.changeBannerStatus(item.id, !item.status);
        const updated = localData.map((banner) =>
          banner.id === item.id ? { ...banner, status: !item.status } : banner
        );
        updateLocalDataCache(updated);
        showSwal("Success!", "Banner status updated.", "success");
      } catch (error) {
        console.error("Status update failed:", error);
        showSwal("Error!", "Failed to update banner status.", "error");
      }
    },
    [localData, showSwal]
  );

  return (
  <DashboardWrapper>
    <div
      className="container-fluid"
      style={{
        marginTop: "10px",
        paddingTop: "0",
        paddingBottom: "0",
      }}
    >
      {/* TOP ROW */}
      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          marginBottom: "8px",
          padding: "0 4px",
        }}
      >
        {/* Sliders text - LEFT */}
        <h4
          className="mb-0"
          style={{
            fontSize: "20px",
            fontWeight: "500",
            color: "#374151",
          }}
        >
          Sliders
        </h4>

        {/* Breadcrumb - RIGHT */}
        <div style={{ margin: 0 }}>
          <Breadcrumb />
        </div>
      </div>

      {/* TABLE CARD */}
      <div
        className="card shadow"
        style={{
          marginTop: "0",
          marginBottom: "10px",
        }}
      >
        {/* CARD HEADER */}
        <div
          className="card-header d-flex justify-content-start align-items-center"
          style={{
            padding: "8px 16px",
            minHeight: "50px",
          }}
        >
          {/* Create Slider button - LEFT SIDE */}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/admin/slider/create")}
            style={{
              padding: "7px 16px",
              fontSize: "14px",
             
            }}
          >
            Create Slider
          </button>
        </div>

        {/* TABLE BODY */}
        <div
          className="card-body"
          style={{
            padding: "8px 16px 10px",
          }}
        >
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                minHeight: "150px",
              }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">
                  Loading...
                </span>
              </div>
            </div>
          ) : (
            <CrudTable
              data={localData}
              onEdit={(row) =>
                navigate(`/admin/slider/edit/${row.id}`)
              }
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              columns={[
                "id",
                "banner_title",
                "banner",
                "status",
              ]}
            />
          )}
        </div>
      </div>
    </div>
  </DashboardWrapper>
);
}
